/**
 * Lifecycle phase classification for the TrendPulse curve, drawn as a ribbon at the base of the
 * price pane. Part of the OPERATOR FEATURES set — see operatorFeatures.js for how to turn it off.
 *
 * <h2>Two numbers decide every bar</h2>
 *
 * <b>Corridor percentile</b> — where the curve sits inside its own 50-bar range, 0 at the floor and
 * 1 at the ceiling. <b>Velocity</b> — the one-bar change. Position says where in the cycle the
 * market is; velocity says which way it is going. Neither alone distinguishes a floor being built
 * from a floor being lost.
 *
 * <h2>The four phases do not cover the plane, and that is left visible</h2>
 *
 * The definitions are:
 *
 * <pre>
 *   Phase 1  Accumulation Floor     pct &lt;= 0.25            and velocity &gt;= 0
 *   Phase 2  Bull Expansion         0.25 &lt; pct &lt; 0.75      and velocity &gt;  0
 *   Phase 3  Distribution Ceiling   pct &gt;= 0.75            and velocity &lt;= 0
 *   Phase 4  Bear Contraction       0.25 &lt; pct &lt; 0.75      and velocity &lt;  0
 * </pre>
 *
 * Three states satisfy none of them, and they are not rare:
 *
 * <ul>
 *   <li><b>pct &gt;= 0.75 with velocity &gt; 0</b> — a ceiling being broken through. On real data this
 *       is one of the most common bars on the chart, and it is exactly the breakout an operator
 *       would want coloured.</li>
 *   <li><b>pct &lt;= 0.25 with velocity &lt; 0</b> — a floor being lost, the mirror image.</li>
 *   <li><b>mid-corridor with velocity exactly 0</b> — rare, but real.</li>
 * </ul>
 *
 * <p>Those bars are classified {@link PHASE_NONE} and drawn as a gap in the ribbon rather than
 * being folded into the nearest phase. Folding them would put a "Bull Expansion" colour on a bar
 * the definition does not call one, and the ribbon's whole value is that its colour means exactly
 * what the four rules say. A gap is a question; a wrong colour is an answer.
 *
 * <p>Extending the set to cover them is a one-line change in {@link classifyPhase} — the two
 * obvious readings are "breakout" and "breakdown" — but it is a change to what the study claims,
 * so it is not made here.
 */

export const PHASE_NONE = 0;
export const PHASE_ACCUMULATION = 1;
export const PHASE_BULL = 2;
export const PHASE_DISTRIBUTION = 3;
export const PHASE_BEAR = 4;

/** How many bars the corridor is measured over. */
export const CORRIDOR_WINDOW = 50;

/** Ribbon height in pixels, at the base of the price grid. */
export const RIBBON_HEIGHT = 8;

export const PHASES = {
  [PHASE_ACCUMULATION]: { key: 'accumulation', label: 'Accumulation Floor', color: '#00c853' },
  [PHASE_BULL]: { key: 'bull', label: 'Bull Expansion', color: '#1b5e20' },
  [PHASE_DISTRIBUTION]: { key: 'distribution', label: 'Distribution Ceiling', color: '#ff1744' },
  [PHASE_BEAR]: { key: 'bear', label: 'Bear Contraction', color: '#8e0000' },
};

/**
 * Where the curve sits inside its own trailing window, 0 at the floor and 1 at the ceiling.
 *
 * <p>Returns null before the window is full, and also when the window is dead flat: with no range
 * there is no position inside it, and dividing by zero would make every bar read as a floor.
 */
export function corridorPercentile(values, t, window = CORRIDOR_WINDOW) {
  if (!Array.isArray(values) || t < window - 1) return null;
  let min = Infinity;
  let max = -Infinity;
  for (let i = t - window + 1; i <= t; i++) {
    const v = values[i];
    if (!Number.isFinite(v)) return null;           // a gap makes the window a shorter window
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!(max > min)) return null;
  return (values[t] - min) / (max - min);
}

/** The phase for one bar, or PHASE_NONE where the four rules say nothing. */
export function classifyPhase(pct, velocity) {
  if (pct === null || pct === undefined || !Number.isFinite(velocity)) return PHASE_NONE;
  if (pct <= 0.25) return velocity >= 0 ? PHASE_ACCUMULATION : PHASE_NONE;
  if (pct >= 0.75) return velocity <= 0 ? PHASE_DISTRIBUTION : PHASE_NONE;
  if (velocity > 0) return PHASE_BULL;
  if (velocity < 0) return PHASE_BEAR;
  return PHASE_NONE;                                 // mid-corridor and exactly flat
}

/** One phase per bar, aligned to the chart's bars. */
export function classifyPhases(values, { window = CORRIDOR_WINDOW } = {}) {
  const list = Array.isArray(values) ? values : [];
  return list.map((v, t) => {
    const prev = list[t - 1];
    if (!Number.isFinite(v) || !Number.isFinite(prev)) return PHASE_NONE;
    return classifyPhase(corridorPercentile(list, t, window), v - prev);
  });
}

/**
 * Consecutive bars of the same phase, collapsed into runs.
 *
 * <p>One rect per run rather than per bar, because a full-history chart is eleven thousand bars and
 * eleven thousand canvas elements is a scroll that stutters. Runs of PHASE_NONE are dropped rather
 * than emitted transparent — an element that draws nothing still costs a hit test.
 */
export function phaseRuns(phases) {
  const list = Array.isArray(phases) ? phases : [];
  const runs = [];
  let current = null;
  list.forEach((phase, i) => {
    if (current && current.phase === phase) { current.end = i; return; }
    current = { phase, start: i, end: i };
    runs.push(current);
  });
  return runs.filter(r => r.phase !== PHASE_NONE);
}

export const PHASE_RIBBON_OVERLAY_ID = 'trendpulse-phase-ribbon';

/**
 * The ribbon overlay: a coloured strip locked to the base of the price pane.
 *
 * <p>Pinned in PIXEL space to the bottom of the grid rather than to a price, so it stays put while
 * the y-axis zooms. Anchored to a price it would slide out of the pane the first time anyone
 * scrolled the vertical zoom — which is most of what a reader does with this chart.
 */
export function buildPhaseRibbonOverlay(values, barData, opts = {}) {
  const dates = barData && Array.isArray(barData.dates) ? barData.dates : [];
  const phases = classifyPhases(values, opts);
  const runs = phaseRuns(phases)
    .filter(r => dates[r.start] !== undefined && dates[r.end] !== undefined)
    .map(r => ({
      startDate: dates[r.start],
      endDate: dates[r.end],
      phase: r.phase,
      label: PHASES[r.phase].label,
      color: PHASES[r.phase].color,
      bars: r.end - r.start + 1,
    }));

  return {
    id: PHASE_RIBBON_OVERLAY_ID,
    type: 'phaseribbon',
    label: 'Lifecycle phases',
    height: RIBBON_HEIGHT,
    runs,
  };
}

/** How many bars fell in each phase — the caller reports this so the ribbon can be read. */
export function summarisePhases(phases) {
  const list = Array.isArray(phases) ? phases : [];
  const counts = { [PHASE_NONE]: 0, [PHASE_ACCUMULATION]: 0, [PHASE_BULL]: 0,
    [PHASE_DISTRIBUTION]: 0, [PHASE_BEAR]: 0 };
  list.forEach((p) => { counts[p] = (counts[p] || 0) + 1; });
  return {
    total: list.length,
    counts,
    unclassified: counts[PHASE_NONE],
    rows: Object.keys(PHASES).map(k => ({ ...PHASES[k], phase: Number(k), bars: counts[k] })),
  };
}
